import { EncounterType } from '@hb/shared'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { requestExamPdf } from '../../backend/functions/other'
import { downloadFromStorage } from '../../backend/storage/utils'
import { useGynCareView } from '../../components/Gyn/context'
import { EncounterSelection } from './types'

export interface ExamListGroupContextData {
  patientId: string
  openExam: EncounterSelection | null
  isSelecting: boolean
  selectedIds: Partial<Record<EncounterType, string[]>>
  onToggleExam: (type: EncounterType, examId: string) => void
  onToggleAll: (type: EncounterType, examIds: string[], allSelected: boolean) => void
  onStartSelecting: () => void
  onCancelSelecting: () => void
  totalSelected: number
  printing: boolean
  onOpenExam: (exam: EncounterSelection) => void
  onPrintSelected: () => Promise<void>
}

export const ExamListGroupContext = createContext<ExamListGroupContextData>({
  patientId: '',
  openExam: null,
  isSelecting: false,
  selectedIds: {},
  onToggleExam: () => {},
  onToggleAll: () => {},
  onStartSelecting: () => {},
  onCancelSelecting: () => {},
  onOpenExam: () => {},
  totalSelected: 0,
  printing: false,
  onPrintSelected: async () => {},
})

export const ExamListGroupProvider = ({
  children,
  patientId,
}: {
  children: React.ReactNode
  patientId: string
}) => {
  const { practiceId } = useGynCareView()
  const [openExam, setOpenExam] = useState<EncounterSelection | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Partial<Record<EncounterType, string[]>>>({})
  const [printing, setPrinting] = useState(false)

  const totalSelected = useMemo(
    () => Object.values(selectedIds).reduce((sum, ids) => sum + (ids?.length ?? 0), 0),
    [selectedIds],
  )

  const handleToggleExam = useCallback((type: EncounterType, examId: string) => {
    setSelectedIds(prev => {
      const current = prev[type] ?? []
      const next = current.includes(examId)
        ? current.filter(id => id !== examId)
        : [...current, examId]
      return { ...prev, [type]: next }
    })
  }, [])

  const handleToggleAll = useCallback(
    (type: EncounterType, examIds: string[], allSelected: boolean) => {
      setSelectedIds(prev => ({ ...prev, [type]: allSelected ? [] : examIds }))
    },
    [],
  )

  const handleCancel = useCallback(() => {
    setIsSelecting(false)
    setSelectedIds({})
  }, [])

  const handlePrintSelected = useCallback(async () => {
    if (!practiceId || !patientId) return
    const allIds = Object.values(selectedIds).flat()
    if (allIds.length === 0) return
    setPrinting(true)
    try {
      const { data } = await requestExamPdf({ practiceId, patientId, examIds: allIds })
      await downloadFromStorage(data.storagePath)
      setIsSelecting(false)
      setSelectedIds({})
    } catch (err) {
      console.error('Failed to generate exams PDF', err)
    } finally {
      setPrinting(false)
    }
  }, [practiceId, patientId, selectedIds])

  return (
    <ExamListGroupContext.Provider
      value={{
        patientId,
        openExam,
        isSelecting,
        selectedIds,
        onToggleExam: handleToggleExam,
        onToggleAll: handleToggleAll,
        onStartSelecting: () => setIsSelecting(true),
        onCancelSelecting: handleCancel,
        onOpenExam: setOpenExam,
        totalSelected,
        printing,
        onPrintSelected: handlePrintSelected,
      }}>
      {children}
    </ExamListGroupContext.Provider>
  )
}

export const useExamListGroup = () => useContext(ExamListGroupContext)
